// ============================================================
//  src/admin/pages/ads-banners/AdminAdsBannersPage.js
// ============================================================
import React, { useEffect, useState, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  getAdsBanners,
  postAddAdsBanner,
  postDeleteAdsBanner,
  postUpdateAdsBannerStatus,
} from '../../api/adminClient';
import {
  SectionHeader, AdminCard, AdminBtn, AdminModal,
  AdminField, AdminInput, AdminPageLoader, AdminBadge,
} from '../../components/AdminUI';
import './AdminAdsBannersPage.css';

// Resolve relative image_path to a full URL using the API base
const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '');
const resolveImg = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;          // already absolute
  return API_ORIGIN + (path.startsWith('/') ? path : '/' + path);
};

export default function AdminAdsBannersPage() {
  const [banners, setBanners]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [saving,  setSaving]      = useState(false);
  const [preview, setPreview]     = useState(null);
  const fileRef                   = useRef();

  const [form, setForm] = useState({ title: '', link: '', status: 'yes' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAdsBanners();
      setBanners(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch { toast.error('Failed to load banners'); }
    finally   { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ title: '', link: '', status: 'yes' });
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleAdd = async () => {
    if (!form.title.trim())         return toast.error('Banner title is required');
    if (!fileRef.current?.files[0]) return toast.error('Please select a banner image');

    const fd = new FormData();
    fd.append('title',        form.title);
    fd.append('link',         form.link);
    fd.append('status',       form.status);
    fd.append('banner_image', fileRef.current.files[0]);

    setSaving(true);
    try {
      await postAddAdsBanner(fd);
      toast.success('Banner added successfully');
      setShowAdd(false);
      resetForm();
      load();
    } catch (e) { toast.error(e?.response?.data?.msg || 'Failed to add banner'); }
    finally     { setSaving(false); }
  };

  const handleToggle = async (banner) => {
    const newStatus = banner.status === 'yes' ? 'no' : 'yes';
    // Optimistic update
    setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, status: newStatus } : b));
    try {
      await postUpdateAdsBannerStatus({ id: banner.id, current_status: banner.status });
    } catch {
      toast.error('Failed to update status');
      // Revert
      setBanners(prev => prev.map(b => b.id === banner.id ? { ...b, status: banner.status } : b));
    }
  };

  const handleDelete = async (banner) => {
    if (!window.confirm(`Delete "${banner.title}"? This cannot be undone.`)) return;
    try {
      await postDeleteAdsBanner({ id: banner.id });
      toast.success('Banner deleted');
      setBanners(prev => prev.filter(b => b.id !== banner.id));
    } catch { toast.error('Failed to delete banner'); }
  };

  if (loading) return <AdminPageLoader />;

  return (
    <div>
      <SectionHeader
        title="Ads Banners"
        subtitle="Manage promotional banners shown to users"
        action={
          <AdminBtn variant="primary" icon="fa-plus" onClick={() => { resetForm(); setShowAdd(true); }}>
            Add New Banner
          </AdminBtn>
        }
      />

      <AdminCard>
        {banners.length === 0 ? (
          <div className="adb-empty">
            <i className="fas fa-image" />
            <p>No banners yet. Add one to get started.</p>
          </div>
        ) : (
          <div className="adb-grid">
            {banners.map((b, i) => (
              <div key={b.id} className="adb-card">
                {/* Banner image */}
                <div className="adb-card-img-wrap">
                  <img src={resolveImg(b.image_path)} alt={b.title} className="adb-card-img" />
                  <span className={`adb-card-badge ${b.status === 'yes' ? 'adb-badge--active' : 'adb-badge--inactive'}`}>
                    {b.status === 'yes' ? 'Active' : 'Inactive'}
                  </span>
                  <span className="adb-card-num">#{i + 1}</span>
                </div>

                {/* Info */}
                <div className="adb-card-body">
                  <p className="adb-card-title">{b.title}</p>
                  {b.link ? (
                    <a className="adb-card-link" href={b.link} target="_blank" rel="noreferrer">
                      <i className="fas fa-link" /> {b.link.length > 32 ? b.link.slice(0, 32) + '…' : b.link}
                    </a>
                  ) : (
                    <span className="adb-card-nolink">No link</span>
                  )}
                  <p className="adb-card-date">
                    <i className="fas fa-calendar-alt" /> {new Date(b.date_added).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>

                {/* Actions */}
                <div className="adb-card-actions">
                  <AdminBtn
                    size="sm"
                    variant={b.status === 'yes' ? 'ghost' : 'primary'}
                    icon={b.status === 'yes' ? 'fa-toggle-off' : 'fa-toggle-on'}
                    onClick={() => handleToggle(b)}
                  >
                    {b.status === 'yes' ? 'Deactivate' : 'Activate'}
                  </AdminBtn>
                  <AdminBtn size="sm" variant="danger" icon="fa-trash-can" onClick={() => handleDelete(b)}>
                    Delete
                  </AdminBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminCard>

      {/* ── Add Banner Modal ── */}
      <AdminModal
        open={showAdd}
        onClose={() => { setShowAdd(false); resetForm(); }}
        title="Add New Ads Banner"
        width={560}
      >
        <div className="adb-form">
          <div className="adb-form-grid">
            <AdminField label="Banner Title *">
              <AdminInput
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Summer Sale Banner"
              />
            </AdminField>

            <AdminField label="Click URL / Link" hint="Leave blank if no click action needed">
              <AdminInput
                value={form.link}
                onChange={e => setForm(p => ({ ...p, link: e.target.value }))}
                placeholder="https://example.com"
              />
            </AdminField>

            <AdminField label="Status">
              <div className="adm-sel-wrap">
                <select
                  className="aui-input"
                  value={form.status}
                  onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                >
                  <option value="yes">Active</option>
                  <option value="no">Inactive</option>
                </select>
              </div>
            </AdminField>
          </div>

          <AdminField label="Banner Image *" hint="Allowed: JPG, PNG, GIF, WEBP. Recommended: 1200×400px">
            <div
              className={`adb-dropzone ${preview ? 'adb-dropzone--has-preview' : ''}`}
              onClick={() => fileRef.current?.click()}
            >
              {preview ? (
                <img src={preview} alt="Preview" className="adb-dropzone-preview" />
              ) : (
                <div className="adb-dropzone-placeholder">
                  <i className="fas fa-cloud-upload-alt" />
                  <span>Click to choose an image</span>
                  <small>JPG, PNG, GIF, WEBP</small>
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {preview && (
              <button className="adb-remove-img" onClick={() => { setPreview(null); fileRef.current.value = ''; }}>
                <i className="fas fa-xmark" /> Remove image
              </button>
            )}
          </AdminField>

          <div className="adb-modal-footer">
            <AdminBtn variant="primary" icon="fa-upload" loading={saving} onClick={handleAdd}>
              Upload Banner
            </AdminBtn>
            <AdminBtn variant="ghost" onClick={() => { setShowAdd(false); resetForm(); }}>
              Cancel
            </AdminBtn>
          </div>
        </div>
      </AdminModal>
    </div>
  );
}